import { ReactNode, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { useServerTranslations } from 'services/rest-api.service';
import { AVAILABLE_LOCALES } from '../i18n/localeConfig';
import { defaultConfig, IntlContext, LocaleConfig, LocalisationContext } from './LocalisationContext';
import { useAuth0 } from '@auth0/auth0-react';

const DEFAULT_LOCALE = 'en';
const LS_LOCALE_PROP = 'tb360_locale';

// logic to determine the locale code in our configured locale map for a passed in
// locale string
// first tries a full fit then just the language code and returns default locale
// if this did not return a configured locale
const determineSystemLocaleForCode = (localeCode: string): string => {
  let locale = localeCode.toLowerCase().replace('_', '-');
  if (!AVAILABLE_LOCALES[locale]) {
    const components = locale.split('-');
    locale = components[0] || '';
  }
  if (!AVAILABLE_LOCALES[locale]) {
    locale = DEFAULT_LOCALE;
  }
  return locale;
};

// returns (and sets, if required) the key for the local config in use
const getCurrentLocaleConfigKey = (): string => {
  const lsCode = localStorage.getItem(LS_LOCALE_PROP);
  const locale = determineSystemLocaleForCode(lsCode ?? navigator.language);
  if (lsCode !== locale) {
    localStorage.setItem(LS_LOCALE_PROP, locale);
  }
  return locale;
};

// returns the actual locale config object in use
// (as determined by getCurrentLocaleConfigKey)
const getCurrentLocaleConfig = () => AVAILABLE_LOCALES[getCurrentLocaleConfigKey()] ?? defaultConfig;

const updateLocale = (locale: string): LocaleConfig => {
  localStorage.setItem(LS_LOCALE_PROP, locale.trim().toLowerCase());
  // execute getLocaleConfigKey to assure valid locale in local storage
  return getCurrentLocaleConfig();
};

export function LocalisationProvider({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth0();
  const [localeConfig, setLocaleConfig] = useState(getCurrentLocaleConfig);

  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isTranslationsLoaded, setIsTranslationsLoaded] = useState(false);

  const { getAll } = useServerTranslations(localeConfig.messageFile);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const serverPromise = isAuthenticated ? getAll() : Promise.resolve({ data: null });

    const localPromise = import(`../i18n/${localeConfig.messageFile}.json`) as Promise<{
      default: Record<string, string>;
    }>;

    Promise.all([serverPromise, localPromise])
      .then(([serverResponse, localModule]) => {
        setTranslations({
          ...(serverResponse.data ?? {}),
          ...localModule.default,
        });
        setIsTranslationsLoaded(true);
      })
      .catch(e => {
        setTranslations({});
        setIsTranslationsLoaded(true);
        console.error(`Error while getting translation messages: ${e}`);
      });
  }, [localeConfig, isLoading, isAuthenticated, getAll]);

  const createUpdatedContext = () => ({
    currentConfig: localeConfig,
    setLocale(configKey: string): void {
      setLocaleConfig(updateLocale(configKey));
    },
  });

  const [context, setContext] = useState<LocalisationContext>(createUpdatedContext());

  useEffect(() => {
    setContext(createUpdatedContext());
  }, [localeConfig]);

  useEffect(() => {
    const lang = localeConfig.localeCode.split('-')[0];
    document.documentElement.lang = lang;
  }, [localeConfig]);

  return (
    <IntlContext.Provider value={context}>
      {isTranslationsLoaded && (
        <IntlProvider locale={localeConfig.localeCode} defaultLocale="en" messages={translations}>
          {children}
        </IntlProvider>
      )}
    </IntlContext.Provider>
  );
}
